import { useNavigate } from '@tanstack/react-router';
import { Info, Loader2 } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CarePlanSummaryPreview } from '@apps/web/components/outreaches/care_plan_summary_preview';
import { MULTI_SHIFT_OUTREACH_MAX } from '@apps/web/components/outreaches/edit_shifts_section';
import { OutreachConflictAlert } from '@apps/web/components/outreaches/outreach_conflict_alert';
import {
    type OutreachUrgency,
    OutreachUrgencySelector,
} from '@apps/web/components/outreaches/outreach_urgency_selector';
import {
    createQuickOutreach,
    getQuickOutreachTransferError,
    parseQuickOutreachCreationFailure,
} from '@apps/web/components/outreaches/quick_outreach_creation';
import {
    serializeShiftGroups,
    type ShiftGroup,
} from '@apps/web/components/outreaches/shift_group_editor';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { toast } from '@apps/web/lib/app_notifications';
import { isAutoAssignAllowed } from '@apps/web/lib/auto_assign';
import { EVENTS } from '@apps/web/lib/events';
import { type OutreachCreationAnalyticsContext } from '@apps/web/lib/outreach_creation_analytics';
import { orderShiftsForOutreach } from '@apps/web/lib/outreach_shift_ordering';
import {
    hasSelectedOutreachConflictShift,
    type OutreachConflictError,
    showOutreachConflictToast,
} from '@apps/web/lib/outreach_utils';
import {
    useOrganization,
    useOrgAxisCareModifyReasons,
    useOutreaches,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type OutreachTemplate,
    type Shift,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const MOBILE_CREATE_ACTION_CLASSNAME =
    'w-full touch-manipulation select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none] sm:w-auto';

function clearTextSelection() {
    window.getSelection()?.removeAllRanges();
}

type QuickOutreachFormProps = {
    readonly selectedShiftIds: Array<string>;
    readonly selectedShifts: Array<Shift>;
    readonly shiftGroups: ReadonlyArray<ShiftGroup> | null;
    readonly timezone: string | undefined;
    readonly onClose: () => void;
    readonly onSuccess?: (outreachId: string | null) => void;
    readonly navigateOnSuccess?: boolean;
    readonly carePlanSummary?: string;
    readonly carePlanBullets?: string;
    readonly perShiftCarePlans?: Record<
        string,
        { readonly summary: string; readonly bullets: string }
    >;
    readonly isLoadingCarePlan?: boolean;
    readonly outreachType?: 'callout' | 'recurring_shift';
    readonly autoAssignShiftIds: ReadonlySet<string>;
    readonly autoAssignNotifyNonResponders?: boolean;
    readonly autoAssignModifyReasonId?: number | null;
    readonly selectedTemplate?: OutreachTemplate;
    readonly analyticsContext?: OutreachCreationAnalyticsContext;
    readonly exceedsShiftLimit?: boolean;
    readonly selectedShiftCount?: number;
    readonly shiftLimit?: number;
    readonly creationBlockedReason?: string;
    // Called with the offending shift ids when the API rejects the submission
    // because a selected shift is cancelled, so the composer can refetch them
    // and surface the stale rows.
    readonly onCancelledShiftSelection?: (
        shiftIds: ReadonlyArray<string>,
    ) => void;
};

export function QuickOutreachForm({
    selectedShiftIds,
    selectedShifts,
    shiftGroups,
    timezone,
    onClose,
    onSuccess,
    navigateOnSuccess = true,
    carePlanSummary,
    carePlanBullets,
    perShiftCarePlans,
    isLoadingCarePlan,
    outreachType,
    autoAssignShiftIds,
    autoAssignNotifyNonResponders = false,
    autoAssignModifyReasonId = null,
    selectedTemplate,
    analyticsContext,
    exceedsShiftLimit,
    selectedShiftCount,
    shiftLimit = MULTI_SHIFT_OUTREACH_MAX,
    creationBlockedReason,
    onCancelledShiftSelection,
}: QuickOutreachFormProps) {
    const organizationId = useOrganizationId();
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'quick-callout-form',
    });
    const autoAssignAllowed = isAutoAssignAllowed(
        organization?.callout_settings,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conflictError, setConflictError] =
        useState<OutreachConflictError | null>(null);
    const [conflictFallbackShiftIds, setConflictFallbackShiftIds] = useState<
        ReadonlyArray<string>
    >([]);
    const [outreachReasonChoice, setOutreachReasonChoice] =
        useState<string>('');
    const [outreachReasonOther, setOutreachReasonOther] = useState<string>('');
    // The org-level default seeds the toggle; once the user flips it their
    // choice wins. Tracked as an override (not synced state) because the
    // organization loads asynchronously after the form mounts.
    const [autoStartOutreachOverride, setAutoStartOutreachOverride] = useState<
        boolean | null
    >(null);
    const [outreachUrgency, setOutreachUrgency] =
        useState<OutreachUrgency>('normal');
    const [autoMonitorOutreach, setAutoMonitorOutreach] =
        useState<boolean>(false);
    const urgentOutreachSupported =
        organization?.urgent_outreach_available === true;
    useEffect(() => {
        if (!urgentOutreachSupported) {
            setOutreachUrgency('normal');
        }
    }, [urgentOutreachSupported]);
    // For AxisCare orgs the Open Shift Reason list is sourced from the
    // account's real AxisCare modification-reason catalog (rather than the
    // generic callout_settings list), so the chosen reason maps to a real
    // AxisCare code that Phoebe writes back on assignment.
    const isAxisCareOrg =
        organization?.general_settings?.primary_data_source === 'axiscare';
    const { data: axiscareModifyReasonsData } = useOrgAxisCareModifyReasons(
        organizationId,
        { reason: 'quick-callout-form' },
        { enabled: isAxisCareOrg },
    );
    const axiscareReasons = useMemo(
        () => axiscareModifyReasonsData?.reasons ?? [],
        [axiscareModifyReasonsData?.reasons],
    );
    const { isReady: trainingModeReady, isInTrainingMode: inTrainingMode } =
        useTrainingMode({
            organizationId,
            reason: 'quick-callout-form',
        });
    // Rule, not decoration: auto-start is unavailable during training, and
    // "still loading" must not read as "not training" — a toggle flipped
    // before the org resolves would otherwise submit auto_start_outreach on
    // a training org. Coerced here (not just via the disabled switch) so the
    // submit body can never carry a stale true. Orgs that turned off
    // agent-created outreach also cannot auto-start (the server rejects the
    // stamp; this keeps the UI honest).
    const agentOutreachEnabled =
        organization?.callout_settings?.agent_outreach_enabled ?? true;
    const autoStartAllowed =
        trainingModeReady && !inTrainingMode && agentOutreachEnabled;
    const autoStartOutreach =
        autoStartOutreachOverride ??
        organization?.callout_settings?.auto_start_outreach_default ??
        false;
    const effectiveAutoStartOutreach = autoStartAllowed && autoStartOutreach;
    const autoMonitorSupported = Boolean(
        organization?.features?.auto_monitor_outreach_enabled,
    );
    function handleAutoStartChange(checked: boolean) {
        setAutoStartOutreachOverride(checked);
        posthog?.capture(EVENTS.QUICK_OUTREACH_AUTO_START_TOGGLED, {
            enabled: checked,
            organization_id: organizationId,
            outreach_type: outreachType ?? 'callout',
        });
    }

    function handleAutoMonitorChange(checked: boolean) {
        setAutoMonitorOutreach(checked);
        posthog?.capture(EVENTS.QUICK_OUTREACH_AUTO_MONITOR_TOGGLED, {
            enabled: checked,
            organization_id: organizationId,
            outreach_type: outreachType ?? 'callout',
        });
    }

    const { mutate: mutateOutreaches } = useOutreaches(
        organizationId,
        { reason: 'quick-callout-form' },
        { outreachTypes: ['callout', 'recurring_shift'] },
    );
    const hasValidShiftSelection =
        selectedShiftIds.length > 0 &&
        selectedShifts.length === selectedShiftIds.length;
    const canSubmit =
        hasValidShiftSelection && !isLoadingCarePlan && !creationBlockedReason;
    const orderedSelectedShifts = useMemo(
        () => orderShiftsForOutreach(selectedShifts, shiftGroups),
        [selectedShifts, shiftGroups],
    );
    const orderedSelectedShiftIds = useMemo(
        () => orderedSelectedShifts.map((shift) => shift.id),
        [orderedSelectedShifts],
    );
    const previousShiftIdsRef = useRef<string>('');
    useEffect(() => {
        const currentKey = selectedShiftIds.join(',');
        if (previousShiftIdsRef.current === currentKey) {
            return;
        }
        previousShiftIdsRef.current = currentKey;
        setConflictError((currentConflict) =>
            currentConflict &&
            hasSelectedOutreachConflictShift(
                currentConflict,
                selectedShiftIds,
                conflictFallbackShiftIds,
            )
                ? currentConflict
                : null,
        );
    }, [conflictFallbackShiftIds, selectedShiftIds]);
    const outreachReasonOptions = useMemo(() => {
        // AxisCare must never fall back to the generic options while its
        // catalog is loading: those labels have no AxisCare code to write back.
        if (isAxisCareOrg) {
            return axiscareReasons.map((reason) => reason.label);
        }
        const options = organization?.callout_settings?.options ?? [];
        const hasOther = options.some(
            (option) => option.toLowerCase() === 'other',
        );
        return hasOther ? options : [...options, 'Other'];
    }, [
        isAxisCareOrg,
        axiscareReasons,
        organization?.callout_settings?.options,
    ]);
    // Map an AxisCare reason label back to its numeric code so the chosen
    // Open Shift Reason can be written back on assignment.
    const axiscareReasonIdByLabel = useMemo(() => {
        const map = new Map<string, number>();
        for (const reason of axiscareReasons) {
            map.set(reason.label, reason.id);
        }
        return map;
    }, [axiscareReasons]);
    // Pre-populate the Open Shift Reason from the reason already on the visit,
    // only when every selected shift shares one reason; otherwise leave it
    // blank. Tracks the value it auto-applied so it recomputes when the shift
    // selection changes, but never clobbers a reason the scheduler picked.
    const autoAppliedReasonRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isAxisCareOrg || axiscareReasons.length === 0) {
            return;
        }
        const codes = new Set(
            selectedShifts.map(
                (shift) => shift.external_modification_reason ?? null,
            ),
        );
        const sharedCode = codes.size === 1 ? [...codes][0] : null;
        const effectiveCode =
            codes.size === 1
                ? (sharedCode ??
                  axiscareModifyReasonsData?.default_reason_id ??
                  null)
                : null;
        const sharedLabel =
            effectiveCode !== null
                ? (axiscareReasons.find(
                      (candidate) => candidate.id === Number(effectiveCode),
                  )?.label ?? null)
                : null;
        const isSchedulerChoice =
            outreachReasonChoice !== '' &&
            axiscareReasonIdByLabel.has(outreachReasonChoice) &&
            outreachReasonChoice !== autoAppliedReasonRef.current;
        if (isSchedulerChoice || (sharedLabel ?? '') === outreachReasonChoice) {
            return;
        }
        autoAppliedReasonRef.current = sharedLabel;
        setOutreachReasonChoice(sharedLabel ?? '');
    }, [
        isAxisCareOrg,
        axiscareReasons,
        axiscareModifyReasonsData?.default_reason_id,
        axiscareReasonIdByLabel,
        selectedShifts,
        outreachReasonChoice,
    ]);

    const handleCreateQuickOutreach = async () => {
        assert.isNotNull(organizationId);
        if (creationBlockedReason) {
            toast.error(creationBlockedReason, {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        if (!hasValidShiftSelection) {
            toast.error('Please refresh your selection before continuing.', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        const transferError = getQuickOutreachTransferError(
            selectedTemplate,
            organization?.default_transfer_number,
        );
        if (transferError) {
            toast.error(transferError, {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        let composedOutreachReason: string | null = null;
        if (outreachReasonChoice) {
            if (
                !isAxisCareOrg &&
                outreachReasonChoice.toLowerCase() === 'other'
            ) {
                const customReason = outreachReasonOther.trim();
                if (!customReason) {
                    toast.error('Please provide a custom reason', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                    return;
                }
                composedOutreachReason = `Other: ${customReason}`;
            } else {
                composedOutreachReason = outreachReasonChoice;
            }
        }

        // For AxisCare, the chosen Open Shift Reason maps to a real AxisCare
        // code that is written back on auto-assignment. For other EHRs the
        // reason id still comes from the parent (HHAExchange) selector.
        const resolvedModifyReasonId = isAxisCareOrg
            ? axiscareReasonIdByLabel.get(outreachReasonChoice)
            : (autoAssignModifyReasonId ?? undefined);

        setConflictError(null);
        setConflictFallbackShiftIds([]);
        setIsSubmitting(true);
        try {
            const response = await createQuickOutreach({
                organizationId,
                body: {
                    shift_ids: orderedSelectedShiftIds,
                    outreach_type: outreachType ?? 'callout',
                    callout_reason: composedOutreachReason,
                    outreach_urgency: outreachUrgency,
                    ...(autoAssignAllowed
                        ? {
                              auto_assign_shift_ids: [...autoAssignShiftIds],
                              auto_assign_notify_non_responders:
                                  autoAssignShiftIds.size > 0 &&
                                  autoAssignNotifyNonResponders,
                              auto_assign_modify_reason_id:
                                  autoAssignShiftIds.size > 0
                                      ? resolvedModifyReasonId
                                      : undefined,
                          }
                        : {}),
                    shift_care_plans:
                        perShiftCarePlans &&
                        Object.keys(perShiftCarePlans).length > 0
                            ? Object.fromEntries(
                                  Object.entries(perShiftCarePlans).map(
                                      ([id, cp]) => [
                                          id,
                                          {
                                              summary: cp.summary || null,
                                              bullets: cp.bullets || null,
                                          },
                                      ],
                                  ),
                              )
                            : null,
                    shift_groups: serializeShiftGroups(shiftGroups),
                    auto_start_outreach: effectiveAutoStartOutreach,
                    auto_monitor_outreach:
                        autoMonitorSupported && autoMonitorOutreach,
                    ...(analyticsContext && {
                        analytics_context: analyticsContext,
                    }),
                },
                reason: 'quick-callout-form',
            });

            await mutateOutreaches();
            const outreachId = response?.id;
            const successMessage = effectiveAutoStartOutreach
                ? 'Outreach created. Phoebe will analyze caregivers and start outreach automatically.'
                : 'Outreach created. Phoebe started analyzing caregivers.';
            toast.success(successMessage, {
                ...(outreachId
                    ? {
                          action: {
                              label: 'View',
                              onClick: () =>
                                  navigate({
                                      to: '/outreaches/$callout-id',
                                      params: {
                                          'callout-id': outreachId,
                                      },
                                      search: { openChat: true },
                                  }),
                          },
                      }
                    : undefined),
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            onClose();
            onSuccess?.(outreachId ?? null);

            if (navigateOnSuccess && outreachId) {
                navigate({
                    to: '/outreaches/$callout-id',
                    params: { 'callout-id': outreachId },
                    search: { openChat: true },
                });
            }
        } catch (error) {
            console.error('Error creating automatic outreach:', error);
            const failure = parseQuickOutreachCreationFailure(
                error,
                'Failed to create quick open shift',
            );
            if (failure.kind === 'cancelled_shift') {
                // Our shift data is stale — the shift was cancelled after the
                // composer loaded it. Refetch so the cancelled rows become
                // visible and `creationBlockedReason` locks Search until the
                // coordinator removes them.
                onCancelledShiftSelection?.(failure.shiftIds);
                toast.error(failure.message, {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return;
            }
            if (failure.kind === 'outreach_conflict') {
                setConflictFallbackShiftIds(orderedSelectedShiftIds);
                setConflictError(failure.conflict);
                showOutreachConflictToast(failure.conflict, (outreachId) =>
                    navigate({
                        to: '/outreaches/$callout-id',
                        params: { 'callout-id': outreachId },
                    }),
                );
            } else {
                toast.error(failure.message, {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-full min-w-0 flex-col gap-4 overflow-x-hidden max-sm:pb-24">
            {conflictError && (
                <OutreachConflictAlert
                    conflict={conflictError}
                    timezone={timezone}
                />
            )}
            {urgentOutreachSupported ? (
                <OutreachUrgencySelector
                    value={outreachUrgency}
                    onValueChange={setOutreachUrgency}
                    disabled={isSubmitting}
                />
            ) : null}
            <div className="space-y-2">
                <Label htmlFor="quick-outreach-reason">Open Shift Reason</Label>
                <Select
                    value={outreachReasonChoice}
                    onValueChange={(value) => {
                        setOutreachReasonChoice(value);
                        if (isAxisCareOrg || value.toLowerCase() !== 'other') {
                            setOutreachReasonOther('');
                        }
                    }}
                >
                    <SelectTrigger
                        id="quick-outreach-reason"
                        className="w-full"
                    >
                        <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                        {outreachReasonOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <CarePlanSummaryPreview
                isLoading={isLoadingCarePlan}
                carePlanSummary={carePlanSummary}
                carePlanBullets={carePlanBullets}
                perShiftCarePlans={perShiftCarePlans}
                selectedShifts={orderedSelectedShifts}
                timezone={timezone}
            />
            {!isAxisCareOrg &&
            outreachReasonChoice.toLowerCase() === 'other' ? (
                <div className="space-y-2">
                    <Label htmlFor="quick-outreach-reason-other">
                        Describe the reason
                    </Label>
                    <Input
                        id="quick-outreach-reason-other"
                        value={outreachReasonOther}
                        onChange={(event) =>
                            setOutreachReasonOther(event.target.value)
                        }
                        placeholder="Please describe"
                    />
                </div>
            ) : null}
            {exceedsShiftLimit && (
                <p className="text-destructive text-right text-sm font-medium">
                    You have selected {selectedShiftCount} shifts. Please reduce
                    to {shiftLimit} or fewer to continue.
                </p>
            )}
            {creationBlockedReason && !exceedsShiftLimit && (
                <p
                    id="quick-outreach-blocked-reason"
                    className="text-destructive text-right text-sm font-medium"
                    role="alert"
                >
                    {creationBlockedReason}
                </p>
            )}
            <div
                data-testid="quick-outreach-actions"
                className="bg-background sticky bottom-0 z-10 mt-auto flex flex-col-reverse gap-2 border-t py-3 select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none] max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-[60] max-sm:px-4 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-end sm:gap-3"
            >
                <div className="flex items-center gap-1">
                    <Label
                        htmlFor="quick-outreach-auto-start"
                        className="text-muted-foreground flex items-center gap-2 text-sm font-normal"
                    >
                        <Switch
                            id="quick-outreach-auto-start"
                            checked={effectiveAutoStartOutreach}
                            onCheckedChange={handleAutoStartChange}
                            disabled={isSubmitting || !autoStartAllowed}
                        />
                        Auto-start
                    </Label>
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <button
                                type="button"
                                aria-label="Auto-start guidance"
                                className="text-muted-foreground hover:text-foreground inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors"
                            >
                                <Info className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            align="end"
                            className="max-w-xs text-left text-wrap"
                        >
                            {!agentOutreachEnabled
                                ? 'Auto-start is turned off for your organization (agent-created outreach is disabled in settings)'
                                : inTrainingMode
                                  ? 'Auto-start will be available once you are finished training Phoebe'
                                  : !trainingModeReady
                                    ? 'Loading…'
                                    : 'Have Phoebe automatically start contacting caregivers when it finds them, without waiting for your approval'}
                        </TooltipContent>
                    </Tooltip>
                </div>
                {autoMonitorSupported && (
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild={true}>
                            <Label
                                htmlFor="quick-outreach-auto-monitor"
                                className="text-muted-foreground flex items-center gap-2 text-sm font-normal"
                            >
                                <Switch
                                    id="quick-outreach-auto-monitor"
                                    checked={autoMonitorOutreach}
                                    onCheckedChange={handleAutoMonitorChange}
                                    disabled={isSubmitting}
                                />
                                Auto-monitor
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                            Agent will monitor the outreach after it starts,
                            according to the preferences specified in your
                            playbook.
                        </TooltipContent>
                    </Tooltip>
                )}
                <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className={MOBILE_CREATE_ACTION_CLASSNAME}
                    onPointerUp={clearTextSelection}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    disabled={!canSubmit || isSubmitting || exceedsShiftLimit}
                    // Without this the button is disabled with no
                    // programmatic link to the reason, so a screen reader
                    // reaches a dead end.
                    aria-describedby={
                        creationBlockedReason && !exceedsShiftLimit
                            ? 'quick-outreach-blocked-reason'
                            : undefined
                    }
                    onClick={() => {
                        clearTextSelection();
                        void handleCreateQuickOutreach();
                    }}
                    onPointerUp={clearTextSelection}
                    className={MOBILE_CREATE_ACTION_CLASSNAME}
                >
                    {isSubmitting ? (
                        <span className="flex items-center gap-2 select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none]">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Creating…
                        </span>
                    ) : (
                        'Search'
                    )}
                </Button>
            </div>
        </div>
    );
}
